/**
 * PrintableProvizorie — pagina „AVIZ MEDICO-SPORTIV PROVIZORIU".
 *
 * Ruta: /admin/patients/:id/provizorie-print
 *
 * Câmpuri:
 *  - Nr. Registru (editabil)
 *  - Nume Prenume (pre-completat din pacient, editabil)
 *  - Data nașterii (pre-completat, editabil)
 *  - Nr. Legitimație (editabil)
 *  - Club / Asociația sportivă (pre-completat, editabil)
 *  - Data eliberării (implicit azi, editabil)
 *  - Valabilitate 15 zile (static)
 *
 * Se deschide într-o fereastră nouă; utilizatorul apasă „Printează" pentru A4.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { patientApi } from "../services/api";

const todayIso = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

// Convert ISO YYYY-MM-DD to dd.mm.yyyy for print
const isoToRo = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || "";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
};

const PrintableProvizorie = () => {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const [nrRegistru, setNrRegistru] = useState("");
  const [numePrenume, setNumePrenume] = useState("");
  const [dataNasterii, setDataNasterii] = useState("");
  const [nrLegitimatie, setNrLegitimatie] = useState("");
  const [clubSportiv, setClubSportiv] = useState("");
  const [dataEliberarii, setDataEliberarii] = useState(todayIso());

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await patientApi.getById(id);
        if (!mounted) return;
        setPatient(data);
        const dp = data?.date_personale || {};
        const is = data?.info_sportive || {};
        const nume = `${dp.nume || ""} ${dp.prenume || ""}`.trim().toUpperCase();
        setNumePrenume(nume);
        setDataNasterii(isoToRo(dp.data_nasterii));
        setClubSportiv(is.club_sportiv || "");
        // Pre-completare numar_viza dacă există (util ca „Nr. Registru")
        if (data?.numar_viza) setNrRegistru(String(data.numar_viza));
      } catch (err) {
        console.error("Failed to load patient:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return <div style={{ padding: 40, fontFamily: "serif" }}>Se încarcă avizul provizoriu…</div>;
  }
  if (!patient) {
    return <div style={{ padding: 40, fontFamily: "serif" }}>Pacientul nu a putut fi încărcat.</div>;
  }

  const inputStyle = {
    border: "0",
    borderBottom: "1px dotted #333",
    background: "transparent",
    outline: "none",
    padding: "1pt 4pt",
    fontFamily: "inherit",
    fontSize: "inherit",
    color: "inherit",
  };

  return (
    <>
      <style>{`
        @page { size: A5 portrait; margin: 12mm; }
        html, body { background: #f5f5f5; color: #111; }
        body { font-family: 'Georgia', 'Times New Roman', serif; font-size: 13pt; }
        .prov-wrap { max-width: 520px; margin: 24px auto; padding: 24px; background: #fff; border: 1px solid #ddd; border-radius: 4px; }
        .prov-actions { text-align: right; margin-bottom: 12px; }
        .prov-actions button { padding: 6px 14px; font-size: 12px; cursor: pointer;
          background: #1e293b; color: #fff; border: 0; border-radius: 4px; margin-left: 6px; }
        @media print {
          body { background: #fff; }
          .prov-actions { display: none; }
          .prov-wrap { max-width: 100%; margin: 0; padding: 0; border: 0; }
          .prov-input { border-bottom: 1px dotted transparent !important; }
        }
        h1.prov-title { font-size: 15pt; text-transform: uppercase; margin: 0; letter-spacing: .3pt; font-weight: 700; }
        h2.prov-subtitle { font-size: 13pt; margin: 4pt 0 20pt; font-weight: 600; }
        .prov-row { margin: 12pt 0; display: flex; align-items: baseline; gap: 6pt; flex-wrap: wrap; }
        .prov-label { text-transform: uppercase; letter-spacing: .2pt; font-weight: 500; white-space: nowrap; }
        .prov-input { flex: 1; min-width: 100pt; }
        .prov-static { margin: 18pt 0; font-weight: 600; text-transform: uppercase; letter-spacing: .3pt; }
        .prov-sign { margin-top: 40pt; }
        .prov-sign-line { display: inline-block; border-bottom: 1px solid #333; width: 220pt; height: 30pt; margin-top: 4pt; }
      `}</style>

      <div className="prov-wrap">
        <div className="prov-actions">
          <button onClick={() => window.print()} data-testid="prov-print-btn">
            Printează (Ctrl+P)
          </button>
        </div>

        <h1 className="prov-title">Aviz medico-sportiv provizoriu</h1>
        <h2 className="prov-subtitle">
          Nr. Registru{" "}
          <input
            type="text"
            value={nrRegistru}
            onChange={(e) => setNrRegistru(e.target.value)}
            style={{ ...inputStyle, width: 90 }}
            className="prov-input"
            data-testid="prov-nr-registru"
          />
        </h2>

        <div className="prov-row">
          <span className="prov-label">Nume prenume:</span>
          <input
            type="text"
            value={numePrenume}
            onChange={(e) => setNumePrenume(e.target.value)}
            style={inputStyle}
            className="prov-input"
            data-testid="prov-nume"
          />
        </div>

        <div className="prov-row">
          <span className="prov-label">Data nașterii:</span>
          <input
            type="text"
            value={dataNasterii}
            onChange={(e) => setDataNasterii(e.target.value)}
            style={{ ...inputStyle, width: 120 }}
            className="prov-input"
            data-testid="prov-data-nasterii"
          />
          <span className="prov-label" style={{ marginLeft: 8 }}>Nr. Leg.</span>
          <input
            type="text"
            value={nrLegitimatie}
            onChange={(e) => setNrLegitimatie(e.target.value)}
            style={{ ...inputStyle, width: 90 }}
            className="prov-input"
            data-testid="prov-nr-leg"
          />
        </div>

        <div className="prov-row">
          <span className="prov-label">Club / Asociația sportivă:</span>
          <input
            type="text"
            value={clubSportiv}
            onChange={(e) => setClubSportiv(e.target.value)}
            style={inputStyle}
            className="prov-input"
            data-testid="prov-club"
          />
        </div>

        <div className="prov-static">Apt pentru competiții sportive</div>

        <div className="prov-row">
          <span className="prov-label">Data eliberării:</span>
          <input
            type="text"
            value={dataEliberarii}
            onChange={(e) => setDataEliberarii(e.target.value)}
            style={{ ...inputStyle, width: 120 }}
            className="prov-input"
            data-testid="prov-data-eliberarii"
          />
        </div>

        <div className="prov-static">Valabilitate 15 zile</div>

        <div className="prov-sign">
          <div className="prov-label">Semnătura medic</div>
          <div className="prov-sign-line" />
        </div>
      </div>
    </>
  );
};

export default PrintableProvizorie;
